import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import {
  FlowSessionLiveStatus,
  useFlowSessionLiveStatus,
} from "@/hooks/useFlowSessionLiveStatus";
import { route } from "@/utils/route";
import {
  ActionItemExecutionCard,
  ActionItemExecutionCardProps,
  ActionItemExecutionStatus,
} from "@cohesive/ui-components";
import React, { useCallback } from "react";

export type ActionItemExecutionCardLiveProps = Omit<
  ActionItemExecutionCardProps,
  "status" | "isLoading" | "current_activity" | "onClick"
>;

/**
 * Maps FlowSessionLiveStatus to ActionItemExecutionStatus for the card component
 */
function mapToExecutionStatus(
  status: FlowSessionLiveStatus,
): ActionItemExecutionStatus {
  switch (status) {
    case "pending":
      return "started";
    case "processing":
      return "running";
    case "needs_attention":
      return "needs_attention";
    case "complete":
      return "completed";
    case "error":
      return "failed";
  }
}

/**
 * A wrapper component that enriches ActionItemExecutionCard with live status
 * updates from WebSocket events and provides click-to-navigate functionality.
 */
export const ActionItemExecutionCardLive: React.FC<
  ActionItemExecutionCardLiveProps
> = ({ flow_session_id, skip_reason, started, ...rest }) => {
  const appNavigate = useAppNavigate();

  // For skipped items (no flow_session_id and has skip_reason), don't fetch live status
  const isSkipped = !started && !!skip_reason;

  // Get live status from the hook (only if not skipped)
  const {
    status: liveStatus,
    currentActivity,
    isLoading,
  } = useFlowSessionLiveStatus(isSkipped ? undefined : flow_session_id);

  // Map to ActionItemExecutionStatus for the card component
  const status = mapToExecutionStatus(liveStatus);

  // Handle click to navigate to child session (only if there's a session to navigate to)
  const handleClick = useCallback(() => {
    if (flow_session_id) {
      appNavigate({
        pane: "rightPane",
        to: route(ROUTES.SESSIONS.SESSION, { sessionId: flow_session_id }),
      });
    }
  }, [flow_session_id, appNavigate]);

  // For skipped items, show the skipped state directly
  if (isSkipped) {
    return (
      <ActionItemExecutionCard
        {...rest}
        flow_session_id={flow_session_id}
        started={started}
        skip_reason={skip_reason}
        status="skipped"
        className="border"
      />
    );
  }

  return (
    <ActionItemExecutionCard
      {...rest}
      flow_session_id={flow_session_id}
      started={started}
      status={status}
      current_activity={currentActivity}
      isLoading={isLoading}
      onClick={handleClick}
      className="border"
    />
  );
};

export default ActionItemExecutionCardLive;
